import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, X } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const QUICK_PROMPTS = [
  "Birthday dress for a 5-year-old under ₹1,500",
  "Festive anarkali for an 8-year-old's wedding",
  "Soft everyday cotton for my toddler",
];

const getSessionId = () => {
  let id = localStorage.getItem("lj_stylist_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("lj_stylist_session", id);
  }
  return id;
};

export default function StylistChat() {
  const { stylistOpen, setStylistOpen, setSelectedProduct } = useStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [products, setProducts] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (stylistOpen && products.length === 0) {
      api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
    }
  }, [stylistOpen, products.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, stylistOpen]);

  const suggestionsFor = (text) =>
    products.filter((p) => text.toLowerCase().includes(p.name.toLowerCase())).slice(0, 3);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "stylist", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stylist/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const j = JSON.parse(line.slice(6));
            if (j.t) {
              acc += j.t;
              const snapshot = acc;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "stylist", text: snapshot };
                return copy;
              });
            }
          } catch { /* partial json line */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "stylist", text: "Sorry, my thread got tangled for a moment — could you try that again?" };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200, damping: 15 }}
        onClick={() => setStylistOpen(true)}
        className="fixed bottom-6 left-6 z-[60] flex items-center gap-2 bg-ink text-cream rounded-full pl-4 pr-5 py-3.5 shadow-xl hover:bg-terracotta transition-colors duration-300"
        data-testid="stylist-open-btn"
      >
        <Sparkles className="w-5 h-5 text-gold" />
        <span className="text-sm font-semibold">Ask Baeboo, our stylist</span>
      </motion.button>

      <AnimatePresence>
        {stylistOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-4 sm:left-6 z-[70] w-[calc(100vw-2rem)] max-w-sm h-[540px] max-h-[70vh] bg-cream rounded-[2rem] shadow-2xl border border-ink/10 flex flex-col overflow-hidden"
            data-testid="stylist-panel"
          >
            <div className="bg-ink text-cream px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </span>
                <div>
                  <p className="font-serif font-bold leading-tight">Baeboo</p>
                  <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-cream/60">AI Dress Stylist</p>
                </div>
              </div>
              <button onClick={() => setStylistOpen(false)} className="rounded-full p-1.5 hover:bg-cream/10" data-testid="stylist-close" aria-label="Close stylist">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="stylist-messages">
              {messages.length === 0 && (
                <div>
                  <div className="bg-white rounded-2xl rounded-tl-md p-3.5 text-sm text-charcoal leading-relaxed">
                    Namaste! Tell me your little one's age, the occasion, and a budget — I'll pick dresses from our collection that fit all three.
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {QUICK_PROMPTS.map((q, i) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        className="text-left text-xs bg-mint hover:bg-sage/20 text-ink rounded-full px-3.5 py-2 transition-colors"
                        data-testid={`stylist-quick-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                  <div
                    className={
                      m.role === "user"
                        ? "bg-terracotta text-white rounded-2xl rounded-tr-md px-4 py-2.5 text-sm max-w-[85%]"
                        : "bg-white rounded-2xl rounded-tl-md p-3.5 text-sm text-charcoal leading-relaxed max-w-[92%] whitespace-pre-wrap"
                    }
                  >
                    {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                  {m.role === "stylist" && m.text && suggestionsFor(m.text).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {suggestionsFor(m.text).map((p) => (
                        <button
                          key={p.id}
                          onClick={() => { setSelectedProduct(p); setStylistOpen(false); }}
                          className="w-full flex items-center gap-3 bg-blush hover:bg-peach rounded-xl p-2 transition-colors text-left"
                          data-testid={`stylist-suggestion-${p.id}`}
                        >
                          <img src={p.images[0]} alt={p.name} className="w-10 h-12 object-cover rounded-lg" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-xs font-semibold text-ink truncate">{p.name}</span>
                            <span className="block font-mono text-[10px] text-charcoal/60">{p.category}</span>
                          </span>
                          <span className="font-serif font-bold text-sm text-terracotta">{inr(p.price)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-ink/10 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="e.g. Twirl frock, 6Y, under ₹2,000"
                className="flex-1 rounded-full border border-ink/10 bg-white px-4 py-2.5 text-sm outline-none focus:border-terracotta"
                data-testid="stylist-input"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="rounded-full bg-ink text-cream p-3 hover:bg-terracotta transition-colors disabled:opacity-40"
                data-testid="stylist-send-btn"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
